/**
 * 上傳收檔的過濾規則(kanban 1000553)。移植自國中 chaiwriting-j utils/batchFileScreen.js
 * (該版為 kanban 1000607),判斷順序與不變式原樣沿用,只調整高中自己的門檻與一處格式判斷。
 *
 * 高中改動前的狀況比國中更糟,兩頁各壞一半:
 *
 * · 批次上傳(BatchUpload)**完全沒有任何檢查** —— 沒有大小上限,也沒有格式檢查。
 *   .txt、.docx 選得進來,會被當成圖片收進清單、還被 parseFilename 拿去解析成
 *   座號姓名,一路走到步驟 4。
 * · 單篇上傳(UploadEssay)則是 PDF 與圖片共用同一道 5MB —— 老師的整班掃描 PDF
 *   動輒數十 MB,一律被擋在門外並顯示「檔案過大,請上傳小於 5MB 的圖片或 PDF」。
 *
 * **PDF 不套圖片那道門檻**(另有兩道遠高的防呆天花板,見 PDF_SIZE_LIMIT 與
 * PDF_TOTAL_LIMIT):PDF 不會被原樣上傳,而是先由 expandFilesWithPdf 逐頁展開成
 * 圖片、再各自 compressImageForUpload(長邊 1280)壓縮後才送出。一張稿紙壓完約
 * 數百 KB,幾十 MB 的整班掃描展開壓縮後實際上傳量只有數 MB —— 來源檔多大跟實際
 * 上傳量幾乎無關。張數本來就由呼叫端的 MAX_IMAGES 另外限制。
 *
 * **圖片維持門檻**:圖片是直接解碼到 canvas,大圖的記憶體風險與 PDF 的逐頁展開
 * ＋壓縮是不同路徑。
 */

import { isPdfFile } from "./pdfToImages";

/**
 * 圖片單檔大小上限(PDF 走下面那條)。與國中 chaiwriting-j 相同的 10MB。
 *
 * 這裡原本是 5MB,理由寫的是「與後端 API 上限一致」—— 出處是 commit 7b0457f
 * (2025-08-13),當時把上限從 10MB 降到 5MB 並同批補上 413 訊息。
 * kanban 1000827 兩邊都實測過之後改為 10MB,理由如下。
 *
 * **後端確實有 5MB 的限制,而且還在。** 對不存在的路徑送不同大小的 body、只看
 * 代理層擋不擋:高中 composition-api 5.0MB 回 404、5.5MB 回 413;國中
 * batch-composition-api 是 10MB(10MB 過、11MB 擋)。限制在代理層 —— 三個後端
 * repo 都 grep 不到 max_request_size / client_max_body_size,uvicorn 本身對
 * 請求體也沒有上限,所以只查應用層設定會得到「後端沒有限制」的錯誤結論。
 *
 * **但這道門檻與那個 5MB 之間沒有因果關係。** 前端門檻管的是「選檔當下的原始檔」,
 * 後端上限管的是「壓縮後送出的請求」,中間隔著 compressImageForUpload(長邊 1280、
 * JPEG 0.92)。實測單篇送出的整個請求:兩張 3000x4000、原圖各 4.4MB 的照片,
 * files(2 張)520,430 bytes ＋ originFiles(2 張)523,758 bytes ＋ 表單欄位 173
 * bytes = 1,044,361 bytes,佔 5MB 的 19.9%。門檻設 5 或設 10,送出量都在 1MB
 * 上下 —— 那個數字對應是符號上的巧合,真正在保護的是壓縮。
 *
 * 時間也對得上:壓縮是 7455a2a(2026-04-30)才導入的,5MB 是 2025-08-13 訂的。
 * 訂定當時原檔直送、兩者確實要一致;壓縮導入後這個依賴就不存在了。
 *
 * **已知的取捨**:圖片是直接解碼到 canvas,佔用看的是像素數不是檔案大小。放寬到
 * 10MB 等於允許更大張的圖進來,低階裝置的記憶體壓力會升高。但這個風險原本就在
 * (一張高壓縮的 4MB 照片也可能是 6000x8000),門檻管檔案大小本來就管不住像素。
 * 未在低階裝置實測。
 *
 * UploadEssay 那兩處 413 訊息裡的「5MB」**維持不動** —— 它說的是後端的上限,
 * 那個數字是對的。
 */
export const IMAGE_SIZE_LIMIT = 10 * 1024 * 1024;

/**
 * PDF 的防呆天花板 —— 不是「上傳量」的門檻,是「一次讀進記憶體」的門檻。
 *
 * expandFilesWithPdf 對每份 PDF 開頭就 `await f.arrayBuffer()`,整份檔案一次進
 * 記憶體(不是逐頁串流),這部分的佔用跟來源檔是等比的。上面那段論證說的是上傳量
 * 與來源檔無關,那點成立,但讀檔當下的記憶體是另一回事。
 *
 * 沒有上界的話,幾百 MB 的整學年合併檔可能直接讓分頁 OOM —— JS 攔不到、畫面白掉,
 * 正好回到這張卡要修的「什麼都沒發生」。
 *
 * **這個數字是防呆,不是實測出來的極限**:真正的極限依裝置與可用記憶體而定,沒有量過。
 * 沿用國中現行值(kanban 1000664 依 PM 要求由 200MB 放寬到 400MB,同樣沒有實測)。
 */
export const PDF_SIZE_LIMIT = 400 * 1024 * 1024;

/**
 * 同一次選取的 PDF 合計上限。
 *
 * 單檔上限擋不住累加:expandFilesWithPdf 逐檔處理,但每份的 PDFDocumentProxy 在
 * worker 裡活到被 GC 為止(沒有呼叫 doc.destroy()),兩份各 250MB 都在單檔上限
 * 之內、峰值卻是相加的。
 *
 * **與單檔上限相同(400MB),不另外放寬**:這道門檻擋的是裝置記憶體峰值,而峰值
 * 約等於同時載入的總和,所以「單檔 400MB 是安全上限」與「總和可以更大」不可能
 * 同時成立(國中 kanban 1000690 的結論,一併沿用)。
 */
export const PDF_TOTAL_LIMIT = 400 * 1024 * 1024;

/**
 * 圖片副檔名的補救清單。
 *
 * **這是刻意偏離國中版的一點。** 國中只看 `type.startsWith("image/")`;高中不能
 * 照抄,因為高中批次頁改動前**根本沒有格式檢查**,只看 type 會造成行為倒退 ——
 * 某些系統的檔案總管(以及部分來源的檔案)給出的 `.jpg` type 是空字串,那些檔案
 * 現在傳得上去,只看 type 就會被新加的檢查擋掉,老師會以為是我們弄壞的。
 *
 * 作法比照同檔案裡 isPdfFile 對 PDF 的處理(type 或副檔名任一認得就算)。
 * 這是 type 不可靠時的**補救**,不是格式白名單:type 正常帶得出來的圖片(bmp、
 * tiff 等)照樣由 type 判斷收下,不受這份清單限制。
 */
const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|heic)$/i;

const looksLikeImage = (file: File): boolean =>
  !!file?.type?.startsWith("image/") || IMAGE_EXTENSIONS.test(file?.name || "");

/**
 * 被略過的原因碼。呼叫端要分流時看這個,不要去比對訊息字串 ——
 * 單篇上傳頁的 gtag 事件要分「格式不符」與「檔案過大」,文案改一次就會壞。
 */
export const REJECT_CODE = {
  tooLarge: "tooLarge",
  pdfTooLarge: "pdfTooLarge",
  pdfTotalTooLarge: "pdfTotalTooLarge",
  unsupported: "unsupported",
} as const;

export type RejectCode = (typeof REJECT_CODE)[keyof typeof REJECT_CODE];

const mb = (bytes: number): string => `${bytes / 1024 / 1024}MB`;

/**
 * 使用者看得到的原因。數字一律由上面的常數推導,不要寫死 ——
 * 門檻調了訊息沒跟著調,比沒有訊息更糟。
 *
 * 標點用半形,與高中批次頁既有的文案一致(這些訊息會與 pdfToImages 的
 * ExpandError.reason 在同一個提示框裡並排)。
 */
export const REJECT_REASON: Record<RejectCode, string> = {
  tooLarge: `圖片檔案超過 ${mb(IMAGE_SIZE_LIMIT)},請改用 PDF 或壓縮後再上傳`,
  pdfTooLarge: `PDF 檔案超過 ${mb(PDF_SIZE_LIMIT)},請分批掃描後再上傳`,
  pdfTotalTooLarge: `這次選取的 PDF 合計超過 ${mb(PDF_TOTAL_LIMIT)},請分批上傳`,
  // 不列舉副檔名:批次頁的說明寫「JPG、PNG、PDF」、單篇頁寫「JPG、PNG、GIF、PDF」,
  // 而實際判斷不是照那兩份清單來的 —— 列舉只會跟某一頁對不起來
  unsupported: "不支援的檔案格式,請選擇圖片或 PDF",
};

export interface RejectedFile {
  name: string;
  reason: string;
  code: RejectCode;
}

/**
 * 把選取的檔案分成收得下的與被略過的。
 *
 * 回傳 { accepted, rejected }:accepted 保持原本的選取順序供後續展開;
 * rejected 是 { name, reason, code },呼叫端可直接餵給既有的提示區
 * (格式為「檔名:原因」),要分流時看 code(見 REJECT_CODE)。
 *
 * 判斷順序是先格式後大小 —— 格式就不對的檔案,回報「太大」只會讓人更困惑。
 * size 不明(undefined)時不當成超標:那是檔案物件本身的缺漏,不是老師選錯檔。
 */
export function screenUploadFiles(files: File[]): {
  accepted: File[];
  rejected: RejectedFile[];
} {
  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];
  let pdfBytes = 0; // 本次已收下的 PDF 合計,擋累加造成的記憶體峰值

  for (const file of files) {
    if (isPdfFile(file)) {
      // PDF 不套圖片那道門檻(理由見檔頭),只擋到會讀爆記憶體的量級
      const size = typeof file.size === "number" ? file.size : 0;
      if (size > PDF_SIZE_LIMIT) {
        rejected.push({
          name: file.name,
          reason: REJECT_REASON.pdfTooLarge,
          code: REJECT_CODE.pdfTooLarge,
        });
        continue;
      }
      if (pdfBytes + size > PDF_TOTAL_LIMIT) {
        rejected.push({
          name: file.name,
          reason: REJECT_REASON.pdfTotalTooLarge,
          code: REJECT_CODE.pdfTotalTooLarge,
        });
        continue;
      }
      pdfBytes += size;
      accepted.push(file);
      continue;
    }
    if (!looksLikeImage(file)) {
      rejected.push({
        name: file?.name,
        reason: REJECT_REASON.unsupported,
        code: REJECT_CODE.unsupported,
      });
      continue;
    }
    if (typeof file.size === "number" && file.size > IMAGE_SIZE_LIMIT) {
      rejected.push({
        name: file.name,
        reason: REJECT_REASON.tooLarge,
        code: REJECT_CODE.tooLarge,
      });
      continue;
    }
    accepted.push(file);
  }

  return { accepted, rejected };
}
